import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("bcp_session_token");
    setUser(null);
  };

  // ── Google OAuth ─────────────────────────────────────────────────────
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
  // Enabled when GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET are in .env
  // and Google Cloud Console has the redirect URI configured.
  const loginWithGoogle = (returnPath) => {
    const path = (typeof returnPath === "string" && returnPath.startsWith("/"))
      ? returnPath
      : window.location.pathname;
    localStorage.setItem("bcp_login_return", path);
    // Must use the full backend URL — relative "/api/auth/google" goes to the
    // React dev server (port 3001) not the backend (port 8001) and does nothing.
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
    window.location.href = `${backendUrl}/api/auth/google?redirect=${encodeURIComponent(path)}`;
  };

  // ── OTP Login ────────────────────────────────────────────────────────
  // Used by OtpLoginModal — modal handles the UI, this just exposes the flag
  // so other components can check if user is logged in via OTP or Google
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginReturnPath, setLoginReturnPath] = useState("/account");

  const openLoginModal = (returnPath) => {
    setLoginReturnPath(returnPath || window.location.pathname);
    setLoginModalOpen(true);
  };

  const closeLoginModal = () => setLoginModalOpen(false);

  return (
    <CustomerAuthContext.Provider value={{
      user, setUser, loading,
      logout, loginWithGoogle, refreshUser,
      loginModalOpen, openLoginModal, closeLoginModal, loginReturnPath,
      refresh: check,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export const useCustomerAuth = () => useContext(CustomerAuthContext);